import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Zap,
  Battery,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Square,
  Clock,
  ChevronRight,
  ChevronDown,
  Gauge,
  Thermometer,
  Settings,
  Power,
  Activity,
  Shield,
  Cpu,
  Car,
  CircleDot,
  Monitor,
  Key,
  Bluetooth,
  Wind,
  Eye,
  Volume2,
  Tv,
  RotateCcw,
  Database,
  Lock,
  Unlock,
  HardDrive,
  Sliders
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { BLE_COMMANDS } from "../config/bleConfig";

// Tesla ODIN Service Routines Database - Based on Toolbox 3
const ODIN_ROUTINES = {
  hv_battery: {
    name: "High Voltage Battery",
    icon: "Battery",
    description: "HV Battery Management System routines",
    routines: [
      {
        id: "PROC_HVC_X_PACK-REPLACEMENT",
        name: "HV Battery Pack Replacement",
        description: "Execute sequence of actions needed following HV battery pack replacement",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Verify new pack is installed correctly",
          "Initialize BMS communication",
          "Write pack serial number to vehicle",
          "Calibrate SOC based on brick voltages",
          "Clear all HV-related fault codes",
          "Run contactor cycling test",
          "Verify isolation resistance"
        ]
      },
      {
        id: "PROC_HVC_X_ECU-REPLACEMENT-BACKUP",
        name: "HVC ECU Backup (Before Replacement)",
        description: "Backup HVC data before ECU replacement",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Read HVC configuration data",
          "Store brick calibration values",
          "Save contactor cycle counts",
          "Export lifetime energy data",
          "Backup fault history"
        ]
      },
      {
        id: "PROC_HVC_X_ECU-REPLACEMENT-RESTORE",
        name: "HVC ECU Restore (After Replacement)",
        description: "Restore HVC data after ECU replacement",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Write stored configuration to new HVC",
          "Restore brick calibration values",
          "Set contactor cycle counts",
          "Import lifetime energy data",
          "Clear installation faults"
        ]
      },
      {
        id: "PROC_HVBMS_X_RESET-SOC",
        name: "Reset State of Charge",
        description: "Reset calculated SOC based on open circuit brick voltage",
        type: "procedure",
        critical: false,
        steps: [
          "Read all brick voltages",
          "Calculate pack voltage",
          "Determine SOC from voltage curve",
          "Write new SOC to BMS",
          "Verify SOC display update"
        ]
      },
      {
        id: "PROC_HVBMS_X_RESET-BRICK-CAC",
        name: "Reset Brick CAC",
        description: "Reset all HV battery brick calculated amp-hour capacities to nominal values",
        type: "procedure",
        critical: true,
        steps: [
          "Clear existing CAC values",
          "Write nominal capacity to all bricks",
          "Reset cycle counters",
          "Initiate capacity learning mode"
        ]
      },
      {
        id: "PROC_HVBMS_X_STATE-OF-HEALTH-TEST",
        name: "Battery State of Health Test",
        description: "Start HV battery SOH test or return last results (takes up to 24 hours)",
        type: "procedure",
        critical: false,
        longRunning: true,
        steps: [
          "Verify SOC max < 50%",
          "Verify charger connected (≥6kW AC)",
          "Disable Sentry Mode, Cabin Overheat Protection",
          "Initiate discharge/charge cycle",
          "Calculate degradation percentage",
          "Update SOH display value"
        ]
      },
      {
        id: "PROC_HVC_X_CLEAR-BLOCKED-CONTACTORS",
        name: "Clear Blocked Contactors Alerts",
        description: "Clear alerts preventing contactor operation",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Verify contactors are functional",
          "Clear contactor fault flags",
          "Reset contactor timers",
          "Test contactor operation"
        ]
      },
      {
        id: "PROC_HVC_X_CLEAR-ISOLATION-CONDITION",
        name: "Clear Isolation Counter Alerts",
        description: "Clear HV isolation fault counter alerts",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Verify isolation resistance > 500Ω/V",
          "Clear isolation fault counter",
          "Reset monitoring timers"
        ]
      },
      {
        id: "PROC_VCFRONT_X_HV-DISCHARGE-START",
        name: "Start HV Battery Discharge",
        description: "Begin controlled HV battery discharge procedure",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Open main contactors",
          "Enable discharge resistors",
          "Monitor voltage decay",
          "Verify safe voltage level"
        ]
      },
      {
        id: "PING-TP_HVBMS",
        name: "Ping HVBMS",
        description: "Test if HV Battery Management System is communicating",
        type: "test",
        critical: false
      },
      {
        id: "TEST-RESET_HVBMS",
        name: "Reset HVBMS",
        description: "Soft reset the HV Battery Management System ECU",
        type: "reset",
        critical: true
      }
    ]
  },
  drive_unit: {
    name: "Drive Unit",
    icon: "Cpu",
    description: "Front and Rear Drive Inverter routines",
    routines: [
      {
        id: "PROC_DI_X_IMMOBILIZER-RESET",
        name: "Drive Inverter Immobilizer Reset",
        description: "Reset immobilizer after drive unit replacement",
        type: "procedure",
        critical: true,
        steps: [
          "Verify new drive unit installed",
          "Pair drive unit to vehicle",
          "Reset immobilizer tokens",
          "Verify torque response"
        ]
      },
      {
        id: "PROC_DI_REAR_REPLACEMENT",
        name: "Rear Drive Unit Replacement",
        description: "Complete rear drive unit replacement procedure",
        type: "procedure",
        critical: true,
        steps: [
          "Backup existing DI configuration",
          "Clear DI fault codes",
          "Write VIN to new DI",
          "Calibrate position sensors",
          "Run immobilizer reset",
          "Verify motor operation"
        ]
      },
      {
        id: "PROC_DI_FRONT_REPLACEMENT",
        name: "Front Drive Unit Replacement",
        description: "Complete front drive unit replacement procedure",
        type: "procedure",
        critical: true,
        steps: [
          "Backup existing DI configuration",
          "Clear DI fault codes",
          "Write VIN to new DI",
          "Calibrate position sensors",
          "Run immobilizer reset",
          "Verify motor operation"
        ]
      },
      {
        id: "PING-TP_DI_FRONT",
        name: "Ping Front Drive Inverter",
        description: "Test front drive inverter communication",
        type: "test",
        critical: false
      },
      {
        id: "PING-TP_DI_REAR",
        name: "Ping Rear Drive Inverter",
        description: "Test rear drive inverter communication",
        type: "test",
        critical: false
      },
      {
        id: "TEST-RESET_DI_FRONT",
        name: "Reset Front Drive Inverter",
        description: "Soft reset front drive inverter ECU",
        type: "reset",
        critical: true
      },
      {
        id: "TEST-RESET_DI_REAR",
        name: "Reset Rear Drive Inverter",
        description: "Soft reset rear drive inverter ECU",
        type: "reset",
        critical: true
      }
    ]
  },
  charging: {
    name: "Charging System",
    icon: "Zap",
    description: "Charge Port, Onboard Charger, DC-DC Converter",
    routines: [
      {
        id: "PROC_CP_X_CALIBRATE-DOOR",
        name: "Calibrate Charge Port Door",
        description: "Calibrate charge port door position sensors",
        type: "procedure",
        critical: false,
        steps: [
          "Cycle door open/close 3 times",
          "Learn door limit positions",
          "Calibrate latch sensor",
          "Test door operation"
        ]
      },
      {
        id: "PROC_CP_X_PLC-RELAY-RETROFIT",
        name: "CCS Retrofit Configuration",
        description: "Configure vehicle for CCS charging retrofit",
        type: "procedure",
        critical: true,
        steps: [
          "Verify CCS hardware installed",
          "Enable CCS communication protocol",
          "Configure PLC relay",
          "Test DC fast charge handshake"
        ]
      },
      {
        id: "PROC_CP_X_ECU-REPLACEMENT",
        name: "Charge Port ECU Replacement",
        description: "Configure new charge port ECU after replacement",
        type: "procedure",
        critical: true,
        steps: [
          "Initialize new CP ECU",
          "Write vehicle configuration",
          "Calibrate door sensors",
          "Test charge detection",
          "Verify latch operation"
        ]
      },
      {
        id: "PROC_PCS_X_ONBOARD-CHARGER-REPLACEMENT",
        name: "Onboard Charger Replacement",
        description: "Configure new onboard charger after replacement",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Verify charger installation",
          "Write charger serial number",
          "Configure charge power limits",
          "Test AC charging capability",
          "Verify thermal management"
        ]
      },
      {
        id: "PROC_PCS_X_DCDC-REPLACEMENT",
        name: "DC-DC Converter Replacement",
        description: "Configure new DC-DC converter after replacement",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Verify DC-DC installation",
          "Write converter serial number",
          "Calibrate output voltage",
          "Test 12V system charging",
          "Verify efficiency parameters"
        ]
      },
      {
        id: "TEST-SELF_PCS_X_CHARGER-HEALTH",
        name: "Onboard Charger Health Test",
        description: "Test onboard charger functionality",
        type: "test",
        critical: false
      },
      {
        id: "TEST-SELF_PCS_X_DCDC-OUTPUT",
        name: "DC-DC Output Test",
        description: "Test DC-DC converter output voltage and current",
        type: "test",
        critical: false
      }
    ]
  },
  pyrotechnic: {
    name: "Pyrotechnic Safety",
    icon: "Shield",
    description: "Pyrotechnic Fuse and HV Safety Disconnect",
    routines: [
      {
        id: "PROC_PYRO_X_FUSE-REPLACEMENT",
        name: "Pyrotechnic Fuse Replacement",
        description: "Configure vehicle after pyrotechnic fuse replacement",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        dangerLevel: "extreme",
        steps: [
          "⚠️ VERIFY HV SYSTEM ISOLATED",
          "Confirm fuse installation correct",
          "Clear pyrotechnic deployment flag",
          "Reset HV safety monitoring",
          "Run HVIL circuit test",
          "Verify contactor operation",
          "Clear all HV fault codes",
          "Test emergency disconnect"
        ]
      },
      {
        id: "PROC_PYRO_X_STATUS-CHECK",
        name: "Pyrotechnic Fuse Status Check",
        description: "Verify pyrotechnic fuse circuit integrity",
        type: "test",
        critical: true,
        requiresHVCert: true
      },
      {
        id: "PROC_HVP_X_FORCE-HVIL-ON",
        name: "Force HVIL On",
        description: "Force HV Interlock Loop current source for testing",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Enable HVIL current source",
          "Monitor loop resistance",
          "Verify all HVIL points",
          "Disable current source"
        ]
      }
    ]
  },
  air_suspension: {
    name: "Air Suspension",
    icon: "Car",
    description: "Tesla Air Suspension (TAS) routines",
    routines: [
      {
        id: "PROC_TAS_X_MEASURE-AIR-SUSPENSION-PRESSURE",
        name: "Measure Air Suspension Pressure",
        description: "Measure pressure in all air springs",
        type: "procedure",
        critical: false,
        steps: [
          "Read front left pressure",
          "Read front right pressure",
          "Read rear left pressure",
          "Read rear right pressure",
          "Compare to specifications"
        ]
      },
      {
        id: "PROC_TAS_X_CLEAR-SPRING-LEAK-ALERT",
        name: "Clear Air Spring Leak Alert",
        description: "Clear air spring leak detection alerts",
        type: "procedure",
        critical: false,
        steps: [
          "Verify leak is repaired",
          "Clear leak alert flag",
          "Reset monitoring baseline"
        ]
      },
      {
        id: "PROC_TAS_X_DEFLATE",
        name: "Deflate All Air Springs",
        description: "Deflate all air suspension springs for service",
        type: "procedure",
        critical: false,
        steps: [
          "Open all exhaust valves",
          "Monitor pressure decrease",
          "Verify full deflation",
          "Close exhaust valves"
        ]
      },
      {
        id: "PROC_TAS_X_CALIBRATE-HEIGHT",
        name: "Calibrate Ride Height Sensors",
        description: "Calibrate ride height sensors after replacement",
        type: "procedure",
        critical: false,
        steps: [
          "Position vehicle on level surface",
          "Set to standard ride height",
          "Calibrate front sensors",
          "Calibrate rear sensors",
          "Verify height settings"
        ]
      },
      {
        id: "PROC_TAS_X_COMPRESSOR-REPLACEMENT",
        name: "Air Compressor Replacement",
        description: "Configure vehicle after air compressor replacement",
        type: "procedure",
        critical: false,
        steps: [
          "Initialize new compressor",
          "Calibrate pressure sensor",
          "Test inflation cycle",
          "Verify thermal cutoff"
        ]
      }
    ]
  },
  das: {
    name: "Driver Assist System",
    icon: "Activity",
    description: "Autopilot, Cameras, and Sensors",
    routines: [
      {
        id: "TEST-RESET_DAS",
        name: "Reset DAS ECU",
        description: "Reset Driver Assist System ECU - use after camera replacement",
        type: "reset",
        critical: true,
        steps: [
          "Power cycle DAS computer",
          "Clear calibration data",
          "Reinitialize camera feeds",
          "Begin recalibration process"
        ]
      },
      {
        id: "PROC_DAS_X_CLEAR-CALIBRATION",
        name: "Clear Camera Calibration",
        description: "Clear camera calibration data - use after adjusting camera angles",
        type: "procedure",
        critical: false,
        steps: [
          "Clear stored calibration",
          "Reset camera positions",
          "Initiate auto-calibration"
        ]
      },
      {
        id: "PING-TP_DAS",
        name: "Ping DAS ECU",
        description: "Test DAS ECU communication",
        type: "test",
        critical: false
      },
      {
        id: "PROC_USM_FRONT_PROGRAM-USS-ADDRESSES",
        name: "Program Front USS Addresses",
        description: "Program ultrasonic sensor addresses after replacement",
        type: "procedure",
        critical: false,
        steps: [
          "Enter USS programming mode",
          "Assign addresses to sensors",
          "Verify sensor responses",
          "Exit programming mode"
        ]
      },
      {
        id: "PROC_USM_REAR_PROGRAM-USS-ADDRESSES",
        name: "Program Rear USS Addresses",
        description: "Program rear ultrasonic sensor addresses after replacement",
        type: "procedure",
        critical: false,
        steps: [
          "Enter USS programming mode",
          "Assign addresses to sensors",
          "Verify sensor responses",
          "Exit programming mode"
        ]
      }
    ]
  },
  vehicle_controllers: {
    name: "Vehicle Controllers",
    icon: "Settings",
    description: "VCFRONT, VCLEFT, VCRIGHT, Gateway",
    routines: [
      {
        id: "TEST-RESET_VCFRONT",
        name: "Reset VCFRONT",
        description: "Reset front vehicle controller",
        type: "reset",
        critical: true
      },
      {
        id: "TEST-RESET_VCLEFT",
        name: "Reset VCLEFT",
        description: "Reset left vehicle controller",
        type: "reset",
        critical: true
      },
      {
        id: "TEST-RESET_VCRIGHT",
        name: "Reset VCRIGHT",
        description: "Reset right vehicle controller",
        type: "reset",
        critical: true
      },
      {
        id: "PROC_TEGRA_X_SECETH-UNLOCK",
        name: "Gateway Unlock",
        description: "Unlock gateway for diagnostic communication",
        type: "procedure",
        critical: true,
        steps: [
          "Send unlock request",
          "Authenticate session",
          "Enable diagnostic mode"
        ]
      },
      {
        id: "PROC_VCFRONT_X_CLEAR-POST-CRASH-LOADSHEDDING",
        name: "Clear Post-Crash Load Shed",
        description: "Clear post-collision load shedding after vehicle is safe to re-energize",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Verify vehicle safety",
          "Clear collision flags",
          "Re-enable LV systems",
          "Re-enable HV system"
        ]
      }
    ]
  },
  low_voltage: {
    name: "Low Voltage System",
    icon: "Battery",
    description: "12V Battery and Power Distribution",
    routines: [
      {
        id: "PROC_VCBATT_X_RECONNECT-LV-BATTERY",
        name: "Reconnect LV Battery",
        description: "Reconnect low voltage battery after replacement",
        type: "procedure",
        critical: false,
        steps: [
          "Detect new battery",
          "Initialize BMS communication",
          "Set battery type (AGM/Li-ion)",
          "Reset age counter"
        ]
      },
      {
        id: "PROC_12V_X_BATTERY-REPLACEMENT",
        name: "12V Battery Replacement",
        description: "Configure vehicle after 12V battery replacement",
        type: "procedure",
        critical: false,
        steps: [
          "Write battery installation date",
          "Reset capacity learning",
          "Configure charging profile",
          "Clear low voltage alerts"
        ]
      },
      {
        id: "TEST-SELF_HVP_X_LV-POWER",
        name: "LV Power Test",
        description: "Test low voltage power input and contactor power from VCRIGHT",
        type: "test",
        critical: false
      },
      {
        id: "TEST-SELF_HVP_X_PCS-INTERFACE",
        name: "PCS Interface Test",
        description: "Test circuit integrity of PCS control lines",
        type: "test",
        critical: false
      }
    ]
  },
  // Factory Mode Procedures (from Tesla manual)
  factory_mode: {
    name: "Factory Mode",
    icon: "Monitor",
    description: "Factory and Developer mode actions",
    routines: [
      {
        id: "FACTORY_ENTER",
        name: "Enter Factory Mode",
        description: "Activate Factory Mode for advanced diagnostics",
        type: "procedure",
        critical: true,
        steps: [
          "Connect via Fakra Ethernet to MCU",
          "Set laptop IP to 192.168.90.101",
          "Run diagnostic shell connection",
          "Send Factory Mode activation command"
        ]
      },
      {
        id: "FACTORY_RESET_ALL_DATA",
        name: "Factory Reset All Data",
        description: "Reset all vehicle data to factory defaults",
        type: "procedure",
        critical: true,
        dangerLevel: "extreme",
        requiresHVCert: true,
        steps: [
          "⚠️ BACKUP ALL IMPORTANT DATA FIRST",
          "Confirm vehicle is in safe state",
          "Execute factory reset command",
          "Wait for completion",
          "Vehicle will reboot"
        ]
      },
      {
        id: "FACTORY_OVERRIDE_LIMITS",
        name: "Override Factory Limits",
        description: "Access power and torque limit overrides",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Enter Factory Mode",
          "Navigate to Settings",
          "Modify POWER LIMIT (kW)",
          "Modify TORQUE LIMIT (Nm)",
          "Save and apply changes"
        ]
      },
      {
        id: "FACTORY_DISABLE_SPEED_LIMIT",
        name: "Disable Speed Limit",
        description: "Remove software speed limitations",
        type: "procedure",
        critical: true,
        steps: [
          "Enter Factory Mode",
          "Navigate to Settings",
          "Toggle Speed Limit Off",
          "Confirm action"
        ]
      }
    ]
  },
  developer_mode: {
    name: "Developer Mode",
    icon: "HardDrive",
    description: "Developer and gateway control functions",
    routines: [
      {
        id: "DEV_TURN_RAILS_OFF",
        name: "Turn Rails Off",
        description: "Disable power rails for service",
        type: "procedure",
        critical: true,
        requiresHVCert: true,
        steps: [
          "Enter Developer Mode",
          "Select Gateway Requests",
          "Execute TURN RAILS OFF",
          "Verify power rails disabled"
        ]
      },
      {
        id: "DEV_STOP_12V_SUPPORT",
        name: "Stop 12V Support",
        description: "Disable 12V system for service work",
        type: "procedure",
        critical: true,
        steps: [
          "Enter Developer Mode",
          "Select Gateway Requests",
          "Execute STOP 12V SUPPORT",
          "Verify 12V system offline"
        ]
      },
      {
        id: "DEV_REBOOT_GATEWAY",
        name: "Reboot Gateway",
        description: "Restart the central gateway module",
        type: "reset",
        critical: true,
        steps: [
          "Enter Developer Mode",
          "Select Gateway Requests",
          "Execute REBOOT GATEWAY",
          "Wait for gateway to restart"
        ]
      },
      {
        id: "DEV_RESTART_IC",
        name: "Restart Instrument Cluster",
        description: "Soft restart the instrument cluster",
        type: "reset",
        critical: false
      },
      {
        id: "DEV_REBOOT_IC",
        name: "Reboot Instrument Cluster",
        description: "Hard reboot the instrument cluster",
        type: "reset",
        critical: false
      },
      {
        id: "DEV_RESTART_UI_APP",
        name: "Restart UI Application",
        description: "Restart the MCU user interface",
        type: "reset",
        critical: false
      },
      {
        id: "DEV_REBOOT_CID",
        name: "Reboot Center Display",
        description: "Hard reboot the center information display",
        type: "reset",
        critical: false
      },
      {
        id: "DEV_RESTART_MODEM",
        name: "Restart Modem",
        description: "Restart the cellular modem",
        type: "reset",
        critical: false
      },
      {
        id: "DEV_CLEAR_ENERGY_LOG",
        name: "Clear Energy Log",
        description: "Clear vehicle energy consumption history",
        type: "procedure",
        critical: false
      },
      {
        id: "DEV_SLEEP_TEGRAS",
        name: "Sleep Tegras",
        description: "Put Tegra processors into sleep mode",
        type: "procedure",
        critical: true
      }
    ]
  },
  thermal_system: {
    name: "Thermal System",
    icon: "Thermometer",
    description: "Coolant, refrigerant, and HVAC procedures",
    routines: [
      {
        id: "PROC_VCFRONT_X_THERMAL-COOLANT-AIR-PURGE",
        name: "Coolant Air Purge",
        description: "Remove air from coolant system after fluid service",
        type: "procedure",
        critical: false,
        steps: [
          "Verify coolant level is correct",
          "Start air purge routine",
          "Monitor coolant temperature",
          "Verify no air bubbles remain"
        ]
      },
      {
        id: "PROC_VCFRONT_X_START-THERMAL-FILL-DRAIN",
        name: "Start Thermal Fill/Drain",
        description: "Enable fluid fill or drain mode for thermal system",
        type: "procedure",
        critical: false,
        steps: [
          "Connect fill/drain equipment",
          "Start fill/drain mode",
          "Monitor fluid levels",
          "Stop when complete"
        ]
      },
      {
        id: "PROC_VCFRONT_X_START-THERMAL-FILL-DRAIN-REFRIGERANT",
        name: "Start Refrigerant Fill/Drain",
        description: "Enable refrigerant recovery or charge mode",
        type: "procedure",
        critical: false,
        steps: [
          "Connect refrigerant equipment",
          "Start fill/drain mode",
          "Monitor pressure and charge",
          "Stop when complete"
        ]
      },
      {
        id: "PROC_VCFRONT_X_STOP-THERMAL-FILL-DRAIN",
        name: "Stop Thermal Fill/Drain",
        description: "Exit fluid fill/drain mode",
        type: "procedure",
        critical: false
      },
      {
        id: "TEST-SELF_VCFRONT_X_COOLANT-PUMPS-IDENTIFICATION",
        name: "Coolant Pumps Identification",
        description: "Run test to identify coolant pump operation",
        type: "test",
        critical: false
      },
      {
        id: "TEST-SELF_VCFRONT_X_THERMAL-PERFORMANCE",
        name: "Thermal Performance Test",
        description: "Test overall thermal system performance",
        type: "test",
        critical: false
      },
      {
        id: "TEST-SELF_VCRIGHT_X_HVAC-PERFORMANCE",
        name: "HVAC Performance Test",
        description: "Test HVAC system performance",
        type: "test",
        critical: false
      },
      {
        id: "TEST-SELF_VCFRONT_X_HEAT-PUMP-COMMISSIONING",
        name: "Heat Pump Commissioning",
        description: "Commission heat pump after service",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_VCFRONT_X_RESET-COMPRESSOR-P-METRIC",
        name: "Reset Compressor P-Metric",
        description: "Reset compressor performance metrics after replacement",
        type: "procedure",
        critical: false
      }
    ]
  },
  closures: {
    name: "Closures & Body",
    icon: "Lock",
    description: "Windows, doors, sunroof, and body procedures",
    routines: [
      {
        id: "PROC_VCLEFT_FRONT-L_CALIBRATE-WINDOW",
        name: "Calibrate Window (Driver)",
        description: "Calibrate driver window position sensors",
        type: "procedure",
        critical: false,
        steps: [
          "Close door during calibration",
          "Pinch detection will be disabled",
          "Run calibration sequence",
          "Verify window operation"
        ]
      },
      {
        id: "PROC_VCRIGHT_REAR_DEFROST-RE-ENABLE",
        name: "Re-enable Rear Defrost",
        description: "Re-enable rear window defrost function",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_WIPER_X_POST-REPLACEMENT-PROCEDURES",
        name: "Wiper Module Replacement",
        description: "Configure after wiper module replacement",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_VCLEFT_FRONT-L_CALIBRATE-DOOR-HANDLE",
        name: "Calibrate Door Handle",
        description: "Calibrate door handle (Model S 2012-2020)",
        type: "procedure",
        critical: false,
        steps: [
          "Verify handle installation",
          "Run calibration sequence",
          "Test handle operation"
        ]
      },
      {
        id: "PROC_CID_X_SUNROOF-CALIBRATE",
        name: "Calibrate Sunroof",
        description: "Calibrate sunroof position (Model S 2012-2020)",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_VCFRONT_X_HOMELINK-RETROFIT",
        name: "HomeLink Retrofit",
        description: "Configure HomeLink module after retrofit",
        type: "procedure",
        critical: false,
        steps: [
          "Verify HomeLink module installed",
          "Run retrofit configuration",
          "Test HomeLink functions"
        ]
      },
      {
        id: "TEST-SELF_VCFRONT_X_HOMELINK",
        name: "Test HomeLink",
        description: "Test HomeLink LIN connections",
        type: "test",
        critical: false
      }
    ]
  },
  chassis_steering: {
    name: "Chassis & Steering",
    icon: "Sliders",
    description: "Steering, brakes, and alignment procedures",
    routines: [
      {
        id: "PROC_EPAS_ESP_CLEAR-ANGLE-OFFSETS",
        name: "Clear Steering Angle Offset",
        description: "Clear applied steering angle offset",
        type: "procedure",
        critical: false
      },
      {
        id: "TEST-SELF_EPAS_X_STEERING-CENTER-OFFSET-SERVICE",
        name: "Steering Offset Drive Test",
        description: "Run steering center offset calibration test",
        type: "procedure",
        critical: false,
        steps: [
          "Drive vehicle in straight line",
          "System learns steering center",
          "Apply correction offset"
        ]
      },
      {
        id: "PROC_STEERING_COLUMN_CALIBRATION",
        name: "Calibrate Steering Column",
        description: "Calibrate steering column after service",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_VCSEC_X_TPMS-ERASE-AND-AUTOLEARN",
        name: "Reset TPMS",
        description: "Erase and relearn tire pressure sensor values",
        type: "procedure",
        critical: false,
        steps: [
          "Erase stored values",
          "Enable auto-learn mode",
          "Drive to learn new sensors"
        ]
      },
      {
        id: "TEST_BRAKE_X_STIFFNESS-TEST-SERVICE",
        name: "Brake Stiffness Test",
        description: "Test brake pedal stiffness and feel",
        type: "test",
        critical: false
      },
      {
        id: "PROC_ESP_FRONT-L-REAR-R-BRAKE-BLEED",
        name: "Brake Bleed FL-RR",
        description: "Bleed brakes: Front Left and Rear Right",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_ESP_FRONT-R-REAR-L-BRAKE-BLEED",
        name: "Brake Bleed FR-RL",
        description: "Bleed brakes: Front Right and Rear Left",
        type: "procedure",
        critical: false
      },
      {
        id: "PROC_EPB_X_SERVICE-MODE",
        name: "EPB Service Mode",
        description: "Release electronic parking brake for service",
        type: "procedure",
        critical: true,
        steps: [
          "Ensure vehicle is secured",
          "Release parking brake",
          "Perform brake service",
          "Re-engage parking brake"
        ]
      }
    ]
  },
  headlights: {
    name: "Headlights",
    icon: "Eye",
    description: "Headlight aiming and leveling procedures",
    routines: [
      {
        id: "PROC_VCFRONT_X_WRITE-HEADLIGHT-AIM-CALIBRATED-VALUES",
        name: "Write Headlight Aim Values",
        description: "Write calibrated headlight aim values",
        type: "procedure",
        critical: false,
        steps: [
          "Position vehicle on level surface",
          "Use headlight aiming equipment",
          "Enter calibrated values",
          "Write to vehicle"
        ]
      },
      {
        id: "PROC_VCFRONT_X_TRIGGER-LIVE-AX-HEADLIGHT-LEVELING",
        name: "Live AX Headlight Leveling",
        description: "Trigger live auto-leveling headlight adjustment",
        type: "procedure",
        critical: false
      }
    ]
  },
  seats: {
    name: "Seats",
    icon: "Car",
    description: "Memory seat calibration (Model S 2012-2020)",
    routines: [
      {
        id: "PROC_MSMD_VCSEATD-FRONT-DRV_CALIBRATE-MEMORY-SEAT",
        name: "Calibrate Driver Seat",
        description: "Calibrate driver memory seat positions",
        type: "procedure",
        critical: false,
        steps: [
          "Move seat to all positions",
          "Learn position limits",
          "Save calibration data"
        ]
      },
      {
        id: "PROC_MSMP_VCSEATP-FRONT-PAS_CALIBRATE-MEMORY-SEAT",
        name: "Calibrate Passenger Seat",
        description: "Calibrate passenger memory seat positions",
        type: "procedure",
        critical: false,
        steps: [
          "Move seat to all positions",
          "Learn position limits",
          "Save calibration data"
        ]
      }
    ]
  },
  software: {
    name: "Software",
    icon: "Database",
    description: "Software reinstall and ECU updates",
    routines: [
      {
        id: "SOFTWARE_REINSTALL_FULL",
        name: "Full Software Reinstall",
        description: "Reinstall software on all ECUs (Model S/X)",
        type: "procedure",
        critical: true,
        steps: [
          "⚠️ Do NOT use after MCU replacement",
          "Select Full reinstall type",
          "Wait for all ECUs to update",
          "Vehicle will reboot"
        ]
      },
      {
        id: "SOFTWARE_REINSTALL_CAN_ECU",
        name: "CAN ECU Software Reinstall",
        description: "Reinstall software on CAN ECUs only",
        type: "procedure",
        critical: true
      },
      {
        id: "SOFTWARE_REINSTALL_DAS",
        name: "DAS Software Reinstall",
        description: "Reinstall software on DAS ECUs only",
        type: "procedure",
        critical: true
      }
    ]
  },
  audio_tests: {
    name: "Audio Tests",
    icon: "Volume2",
    description: "Speaker and audio system testing",
    routines: [
      {
        id: "AUDIO_TEST_ALL",
        name: "Test All Speakers",
        description: "Play test tone through all speakers",
        type: "test",
        critical: false
      },
      {
        id: "AUDIO_TEST_SINE",
        name: "Sine Wave Test",
        description: "Play sine wave through selected speakers",
        type: "test",
        critical: false
      }
    ]
  }
};

const ServiceRoutines = ({ connectionStatus, serviceModeStatus, bleManager, bleConnected, bleLiveData }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [showRoutineDialog, setShowRoutineDialog] = useState(false);
  const [routineInProgress, setRoutineInProgress] = useState(false);
  const [routineStep, setRoutineStep] = useState(0);
  const [routineStatus, setRoutineStatus] = useState(null);
  const [safetyAcknowledged, setSafetyAcknowledged] = useState(false);
  const [hvCertAcknowledged, setHvCertAcknowledged] = useState(false);
  const [routineLogs, setRoutineLogs] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeTab, setActiveTab] = useState("service"); // "service" or "factory"

  const getIcon = (iconName) => {
    const icons = {
      Battery: Battery,
      Zap: Zap,
      Cpu: Cpu,
      Shield: Shield,
      Car: Car,
      Activity: Activity,
      Settings: Settings,
      Power: Power,
      Monitor: Monitor,
      HardDrive: HardDrive,
      Thermometer: Thermometer,
      Lock: Lock,
      Sliders: Sliders,
      Eye: Eye,
      Volume2: Volume2,
      Database: Database
    };
    return icons[iconName] || Settings;
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const startRoutine = (routine, categoryKey) => {
    setSelectedRoutine({ ...routine, category: categoryKey });
    setShowRoutineDialog(true);
    setRoutineStep(0);
    setRoutineStatus(null);
    setSafetyAcknowledged(false);
    setHvCertAcknowledged(false);
    setRoutineLogs([]);
  };

  const executeRoutine = async () => {
    if (!safetyAcknowledged) {
      toast.error("Safety acknowledgment required");
      return;
    }
    if (selectedRoutine.requiresHVCert && !hvCertAcknowledged) {
      toast.error("HV certification acknowledgment required");
      return;
    }

    setRoutineInProgress(true);
    setRoutineStatus("running");
    const logs = [];

    try {
      // Log routine start
      logs.push({ time: new Date().toLocaleTimeString(), message: `Starting ${selectedRoutine.id}...`, type: "info" });
      setRoutineLogs([...logs]);

      // If BLE is connected, send command via ESP32 for real CAN execution
      if (bleConnected && bleManager) {
        logs.push({ time: new Date().toLocaleTimeString(), message: `Sending via ESP32 BLE...`, type: "info" });
        setRoutineLogs([...logs]);
        
        // Generate a simple routine ID from the ODIN ID
        const routineId = selectedRoutine.id.split('_').pop()?.charCodeAt(0) || 0x01;
        await bleManager.sendODINRoutine(routineId);
        
        logs.push({ time: new Date().toLocaleTimeString(), message: `Command sent to ESP32`, type: "success" });
        setRoutineLogs([...logs]);
      }

      // Execute steps if procedure
      if (selectedRoutine.steps) {
        for (let i = 0; i < selectedRoutine.steps.length; i++) {
          setRoutineStep(i + 1);
          logs.push({ time: new Date().toLocaleTimeString(), message: `Step ${i + 1}: ${selectedRoutine.steps[i]}`, type: "step" });
          setRoutineLogs([...logs]);
          
          // Simulate step execution (in real scenario, wait for ESP32 response)
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          logs.push({ time: new Date().toLocaleTimeString(), message: `Step ${i + 1} complete`, type: "success" });
          setRoutineLogs([...logs]);
        }
      } else {
        // Simple test/reset - quick execution
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      // Log to backend
      await axios.post(`${API}/service-routines/execute`, {
        routine_id: selectedRoutine.id,
        routine_name: selectedRoutine.name,
        category: selectedRoutine.category,
        status: "completed",
        timestamp: new Date().toISOString(),
        ble_executed: bleConnected
      });

      logs.push({ time: new Date().toLocaleTimeString(), message: `${selectedRoutine.id} completed successfully`, type: "success" });
      setRoutineLogs([...logs]);
      setRoutineStatus("success");
      toast.success(`${selectedRoutine.name} completed successfully`);

    } catch (error) {
      logs.push({ time: new Date().toLocaleTimeString(), message: `Error: ${error.message}`, type: "error" });
      setRoutineLogs([...logs]);
      setRoutineStatus("error");
      toast.error(`Routine failed: ${error.message}`);
    } finally {
      setRoutineInProgress(false);
    }
  };

  const getRoutineTypeColor = (type) => {
    switch (type) {
      case "procedure": return "text-[#007AFF]";
      case "test": return "text-[#30D158]";
      case "reset": return "text-[#FF9F0A]";
      default: return "text-white/60";
    }
  };

  const getRoutineTypeBadge = (type) => {
    switch (type) {
      case "procedure": return "bg-[#007AFF]/20 text-[#007AFF] border-[#007AFF]/30";
      case "test": return "bg-[#30D158]/20 text-[#30D158] border-[#30D158]/30";
      case "reset": return "bg-[#FF9F0A]/20 text-[#FF9F0A] border-[#FF9F0A]/30";
      default: return "bg-white/10 text-white/60 border-white/20";
    }
  };

  if (!connectionStatus.connected) {
    return (
      <div className="fade-in">
        <div className="header-bar">
          <div>
            <h1 className="header-title">Service Routines</h1>
            <p className="header-subtitle">Tesla Toolbox 3 / Service Mode Plus Actions</p>
          </div>
        </div>
        
        <div className="bento-card text-center py-16">
          <Settings className="h-16 w-16 text-white/20 mx-auto mb-4" />
          <p className="text-white/50 text-lg">No vehicle connected</p>
          <p className="text-white/30 text-sm mt-2">Connect to execute service routines</p>
        </div>
      </div>
    );
  }

  // Categorize routines into Service Mode and Factory Mode
  const serviceModeCategories = ['hv_battery', 'drive_unit', 'charging', 'pyrotechnic', 'air_suspension', 'das', 'vehicle_controllers', 'low_voltage', 'thermal_system', 'closures', 'chassis_steering', 'headlights', 'seats', 'software', 'audio_tests'];
  const factoryModeCategories = ['factory_mode', 'developer_mode'];

  const filteredRoutines = Object.entries(ODIN_ROUTINES).filter(([key]) => 
    activeTab === 'factory' ? factoryModeCategories.includes(key) : serviceModeCategories.includes(key)
  );

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="header-bar">
        <div>
          <h1 className="header-title">Service Routines</h1>
          <p className="header-subtitle">ODIN Actions • Tesla Toolbox 3 Compatible</p>
        </div>
        <div className="flex items-center gap-2">
          {serviceModeStatus?.active ? (
            <span className="badge-success flex items-center gap-1">
              <CheckCircle className="h-3 w-3" /> Service Mode Plus
            </span>
          ) : (
            <span className="badge-warning flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Limited Mode
            </span>
          )}
          {bleConnected && (
            <span className="badge-info flex items-center gap-1">
              <Bluetooth className="h-3 w-3" /> ESP32 BLE
            </span>
          )}
        </div>
      </div>

      {/* Mode Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('service')}
          className={`px-4 py-2 rounded-lg font-heading text-sm uppercase tracking-wider transition-colors ${
            activeTab === 'service' 
              ? 'bg-[#007AFF] text-white' 
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
          data-testid="tab-service-mode"
        >
          <Settings className="h-4 w-4 inline mr-2" />
          Service Mode Plus
        </button>
        <button
          onClick={() => setActiveTab('factory')}
          className={`px-4 py-2 rounded-lg font-heading text-sm uppercase tracking-wider transition-colors ${
            activeTab === 'factory' 
              ? 'bg-[#FF9F0A] text-black' 
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
          data-testid="tab-factory-mode"
        >
          <Monitor className="h-4 w-4 inline mr-2" />
          Factory Mode
        </button>
      </div>

      {/* Warning Banner */}
      <div className={`mb-6 p-4 border rounded ${
        activeTab === 'factory' 
          ? 'bg-[#FF3B30]/10 border-[#FF3B30]/30' 
          : 'bg-[#FF9F0A]/10 border-[#FF9F0A]/30'
      }`}>
        <div className="flex items-start gap-4">
          <AlertTriangle className={`h-6 w-6 mt-0.5 ${
            activeTab === 'factory' ? 'text-[#FF3B30]' : 'text-[#FF9F0A]'
          }`} />
          <div>
            <p className={`font-heading uppercase tracking-wider ${
              activeTab === 'factory' ? 'text-[#FF3B30]' : 'text-[#FF9F0A]'
            }`}>
              {activeTab === 'factory' ? '⚠️ FACTORY MODE - ADVANCED USE ONLY' : 'Professional Service Routines'}
            </p>
            <p className="text-white/60 text-sm mt-1">
              {activeTab === 'factory' 
                ? 'Factory Mode procedures can permanently alter vehicle configuration. Only use if you know exactly what you are doing. Incorrect use may brick the vehicle.'
                : 'These routines are equivalent to Tesla Toolbox 3 / Service Mode Plus actions. HV-related procedures require certified technician.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* BLE Live Data Panel (when connected) */}
      {bleConnected && bleLiveData && (
        <div className="mb-6 p-4 glass-card">
          <div className="flex items-center gap-2 mb-3">
            <Bluetooth className="h-4 w-4 text-[#007AFF]" />
            <span className="text-sm text-white/60 uppercase tracking-wider">Live CAN Data from ESP32</span>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-white/40">Pack Voltage</p>
              <p className="font-mono text-lg text-[#007AFF]">{bleLiveData.v?.toFixed(1) || '--'} V</p>
            </div>
            <div>
              <p className="text-xs text-white/40">Pack Current</p>
              <p className="font-mono text-lg text-white">{bleLiveData.i?.toFixed(1) || '--'} A</p>
            </div>
            <div>
              <p className="text-xs text-white/40">SOC</p>
              <p className="font-mono text-lg text-[#30D158]">{bleLiveData.soc?.toFixed(0) || '--'}%</p>
            </div>
            <div>
              <p className="text-xs text-white/40">HV Isolation</p>
              <p className="font-mono text-lg text-white">{bleLiveData.hvi?.toFixed(0) || '--'} kΩ</p>
            </div>
          </div>
        </div>
      )}

      {/* Routine Categories */}
      <div className="space-y-3">
        {filteredRoutines.map(([categoryKey, category]) => {
          const IconComponent = getIcon(category.icon);
          const isExpanded = expandedCategories[categoryKey];
          
          return (
            <div key={categoryKey} className="glass-card overflow-hidden">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(categoryKey)}
                className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                data-testid={`category-${categoryKey}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-[#007AFF]/20 rounded-lg">
                    <IconComponent className="h-6 w-6 text-[#007AFF]" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-heading text-lg uppercase tracking-wider text-white">{category.name}</h3>
                    <p className="text-sm text-white/50">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-white/40 font-mono">{category.routines.length} routines</span>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-white/50" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-white/50" />
                  )}
                </div>
              </button>

              {/* Routines List */}
              {isExpanded && (
                <div className="border-t border-white/10">
                  {category.routines.map((routine) => (
                    <div
                      key={routine.id}
                      className={`p-4 flex items-center justify-between hover:bg-white/5 border-b border-white/5 last:border-b-0 cursor-pointer ${
                        routine.critical ? 'bg-[#FF3B30]/5' : ''
                      }`}
                      onClick={() => startRoutine(routine, categoryKey)}
                      data-testid={`routine-${routine.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <CircleDot className={`h-4 w-4 ${getRoutineTypeColor(routine.type)}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-mono text-sm text-white">{routine.id}</p>
                            <span className={`text-xs px-2 py-0.5 rounded border ${getRoutineTypeBadge(routine.type)}`}>
                              {routine.type.toUpperCase()}
                            </span>
                            {routine.requiresHVCert && (
                              <span className="text-xs px-2 py-0.5 rounded bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/30">
                                HV CERT
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-white/60 mt-1">{routine.name}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#007AFF]/20 text-[#007AFF] hover:bg-[#007AFF]/30 border border-[#007AFF]/30"
                        onClick={(e) => {
                          e.stopPropagation();
                          startRoutine(routine, categoryKey);
                        }}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Run
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Routine Execution Dialog */}
      <Dialog open={showRoutineDialog} onOpenChange={(open) => {
        if (!open && !routineInProgress) {
          setShowRoutineDialog(false);
          setSelectedRoutine(null);
        }
      }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading text-xl uppercase tracking-wider flex items-center gap-3">
              <Play className="h-5 w-5 text-[#007AFF]" />
              Execute Routine
            </DialogTitle>
            <DialogDescription className="text-white/60 font-mono">
              {selectedRoutine?.id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 pr-2">
            {!routineInProgress && routineStatus !== "success" ? (
              <>
                {/* Routine Info */}
                <div className="p-4 glass-card mb-4">
                  <h4 className="font-heading text-lg text-white">{selectedRoutine?.name}</h4>
                  <p className="text-white/60 text-sm mt-1">{selectedRoutine?.description}</p>
                  <div className="flex items-center gap-3 mt-3">
                    <span className={`text-xs px-2 py-1 rounded border ${getRoutineTypeBadge(selectedRoutine?.type)}`}>
                      {selectedRoutine?.type?.toUpperCase()}
                    </span>
                    {selectedRoutine?.critical && (
                      <span className="text-xs text-[#FF9F0A]">⚠️ Critical Operation</span>
                    )}
                    {selectedRoutine?.longRunning && (
                      <span className="text-xs text-white/50">🕐 Long Running</span>
                    )}
                  </div>
                </div>

                {/* HV Warning */}
                {selectedRoutine?.requiresHVCert && (
                  <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded mb-4 warning-flash">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-[#FF3B30] mt-0.5" />
                      <div>
                        <p className="text-[#FF3B30] font-bold">⚡ HIGH VOLTAGE CERTIFICATION REQUIRED</p>
                        <p className="text-white/60 text-sm mt-1">
                          This routine involves high-voltage systems (400V DC). Must be performed by HV-certified technician only.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Steps Preview */}
                {selectedRoutine?.steps && (
                  <div className="mb-4">
                    <p className="text-sm text-white/50 uppercase tracking-wider mb-2">Routine Steps</p>
                    <div className="space-y-1">
                      {selectedRoutine.steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 glass-card">
                          <span className="text-[#007AFF] font-mono text-xs w-5">{i + 1}</span>
                          <span className="text-white/80 text-sm">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Safety Acknowledgments */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-white/5 rounded">
                    <Checkbox
                      id="routine-safety"
                      checked={safetyAcknowledged}
                      onCheckedChange={setSafetyAcknowledged}
                      className="border-white/30 mt-1"
                      data-testid="routine-safety-checkbox"
                    />
                    <label htmlFor="routine-safety" className="text-sm text-white/80 cursor-pointer">
                      I understand this routine may affect vehicle operation. Vehicle is in a safe location and I accept responsibility for any outcomes.
                    </label>
                  </div>

                  {selectedRoutine?.requiresHVCert && (
                    <div className="flex items-start space-x-3 p-3 bg-[#FF3B30]/10 rounded">
                      <Checkbox
                        id="hv-cert"
                        checked={hvCertAcknowledged}
                        onCheckedChange={setHvCertAcknowledged}
                        className="border-[#FF3B30]/50 mt-1"
                        data-testid="hv-cert-checkbox"
                      />
                      <label htmlFor="hv-cert" className="text-sm text-white/80 cursor-pointer">
                        I am a certified HV technician with proper training and PPE. I understand the lethal risks of high-voltage systems.
                      </label>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Execution Progress / Results */
              <div className="space-y-4">
                {/* Status Header */}
                <div className="text-center py-4">
                  {routineStatus === "running" ? (
                    <RefreshCw className="h-12 w-12 text-[#007AFF] mx-auto animate-spin" />
                  ) : routineStatus === "success" ? (
                    <CheckCircle className="h-12 w-12 text-[#30D158] mx-auto" />
                  ) : routineStatus === "error" ? (
                    <XCircle className="h-12 w-12 text-[#FF3B30] mx-auto" />
                  ) : null}
                  <p className="text-lg text-white mt-4">
                    {routineStatus === "running" ? "Executing Routine..." : 
                     routineStatus === "success" ? "Routine Completed" :
                     routineStatus === "error" ? "Routine Failed" : ""}
                  </p>
                  {selectedRoutine?.steps && routineStatus === "running" && (
                    <p className="text-sm text-white/50 mt-1">
                      Step {routineStep} of {selectedRoutine.steps.length}
                    </p>
                  )}
                </div>

                {/* Progress Bar */}
                {selectedRoutine?.steps && (
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(routineStep / selectedRoutine.steps.length) * 100}%` }}
                    ></div>
                  </div>
                )}

                {/* Execution Log */}
                <div className="p-3 bg-black/50 rounded font-mono text-xs max-h-60 overflow-y-auto">
                  {routineLogs.map((log, i) => (
                    <div key={i} className={`py-1 ${
                      log.type === "error" ? "text-[#FF3B30]" :
                      log.type === "success" ? "text-[#30D158]" :
                      log.type === "step" ? "text-[#007AFF]" :
                      "text-white/60"
                    }`}>
                      <span className="text-white/40">[{log.time}]</span> {log.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-shrink-0 pt-4 border-t border-white/10">
            {!routineInProgress && routineStatus !== "success" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRoutineDialog(false);
                    setSelectedRoutine(null);
                  }}
                  className="bg-transparent border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeRoutine}
                  disabled={!safetyAcknowledged || (selectedRoutine?.requiresHVCert && !hvCertAcknowledged)}
                  className="btn-primary"
                  data-testid="execute-routine-btn"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Execute
                </Button>
              </>
            )}
            {routineStatus === "success" && (
              <Button
                onClick={() => {
                  setShowRoutineDialog(false);
                  setSelectedRoutine(null);
                }}
                className="btn-primary"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceRoutines;
